import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Spin, Card, Row, Col, Typography, Tag, Space, Button, Image,
  Alert, Breadcrumb, Tooltip, Modal, Dropdown, Descriptions, Table, message
} from 'antd';
import {
  EnvironmentOutlined, HomeOutlined, InfoCircleOutlined, TagOutlined,
  ToolOutlined, FileDoneOutlined, LeftOutlined, RightOutlined,
  BankOutlined, EditOutlined, DragOutlined, PlusOutlined, DownOutlined, FilePdfOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import minMax from 'dayjs/plugin/minMax';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { getBankLogo } from '../../../utils/bankLogos';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../../../utils/config';
import DataListModal from '../modals/DataListModal';
import ImageModal from '../modals/ImageModal';
import FormikForm from '../../form/FormikForm';
import CumulativeProfitChart from './CumulativeProfitChart';
import { generateProjection, calculateDateRange } from '../../../utils/contractUtils';
import { useContracts } from '../../../hooks/useContracts';
import { getImageUrls } from '../../../utils/poifotoUtils';
import NotlarModal from '../modals/NotlarModal';
import { formatMoney } from '../../../utils/currency';
import { trLower, trIncludes } from '../../../utils/trText';
import OffsiteMap from './OffsiteMap';
import YsOdemeleriPanel from './YsOdemeleriPanel';

dayjs.extend(minMax);
dayjs.locale('tr');

const { Title, Paragraph } = Typography;
const API_BASE_URL = SHARED_API_BASE_URL;
// ongoru_grafik önbelleğinin hangi mali-rapor-* sürümüyle hesaplandığı (slice_config.kaynak).
// Sorgular değişince bu değer artırılır; farklı/eksik işaretli grafik bir kez yeniden hesaplanır.
const MALI_RAPOR_KAYNAK = 'mali-rapor-v2';

const ODEME_TIPLERI = { 0: 'Aylık', 1: 'Yıllık' };
const SOZLESME_IMZA_DURUMU = {
  0: 'İptal (Sözleşme Yapılmadı)',
  1: 'İmzalandı (Yeni)',
  2: 'İmzalandı (Rotasyon)',
  3: 'Feshedildi (İptal)',
  4: 'Feshedildi (Rotasyon)',
  5: 'Feshedildi (Söz. Yenileme)',
  6: 'İmzalandı (Söz. Yenileme)',
  7: 'Belediye Kiracısı',
  99: 'İmzalanmadı (Aday)',
};

const parseContractDate = (value) => {
  if (!value || value === '00/00/0000' || value === '0000-00-00') return null;
  const trDate = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  const parsed = trDate
    ? dayjs(`${trDate[3]}-${trDate[2]}-${trDate[1]}`)
    : dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const contractCodeLabel = (value, labels) => labels[value] || value || '-';

const getDurumColor = (durum) => {
  switch (trLower(durum)) {
    case 'aktif': return '#52c41a';
    case 'pasif': return '#f5222d';
    default: return '#1890ff';
  }
};

const parseCoordinate = (coord) => {
  if (coord == null) return null;
  if (typeof coord === 'number') return coord;
  const cleaned = coord.replace(/[^\d.,-]/g, '').replace(/,/g, '.').replace(/\.+/g, '.').replace(/\.$/, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
};

function KabinlerListesi({ data = [], platforms = [], user = null, onOpenFormModal = null, currentLokasyon = null }) {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const organizedPlatforms = useMemo(() => {
    if (!Array.isArray(platforms) || platforms.length === 0) return [];

    const cabinsByPlatform = data.reduce((acc, cabin) => {
      const pid = cabin.platformId || cabin.platform_id;
      if (!acc[pid]) acc[pid] = [];
      acc[pid].push(cabin);
      return acc;
    }, {});

    const sorted = [...platforms].sort((a, b) => {
      const dateDiff = (new Date(a.kurulmaTarihi || 0)) - (new Date(b.kurulmaTarihi || 0));
      if (dateDiff !== 0) return dateDiff;
      return ((a.sira ?? 0) - (b.sira ?? 0)) || ((a.id ?? 0) - (b.id ?? 0));
    });

    return sorted.map((p) => {
      const pid = p.id ?? p.platform_id ?? p.platformId;
      const kabinSayisi = (p.kabin_count ?? p.kabin ?? 0) || 0;
      const platformCabins = cabinsByPlatform[pid] || [];
      const kabinler = [];

      for (let i = 1; i <= kabinSayisi; i++) {
        const mevcut = platformCabins.find(k =>
          k.platformCabinIndex === i || k.kabinSiraNo === i || parseInt(k.kabinNo) === i
        );
        kabinler.push({ siraNo: i, kabin: mevcut, isEmpty: !mevcut });
      }

      return {
        id: pid,
        sira: p.sira ?? p.platform_sira,
        kabinSayisi,
        kabinler,
        enerji: p.enerji || currentLokasyon?.enerji,
        // 20.08 madde 14: boş kabin kutuları da abone/sayaç gösterebilsin
        kabinEnerji: p.kabinEnerji || {},
        originalPlatform: p
      };
    });
  }, [platforms, data, currentLokasyon]);

  if (organizedPlatforms.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '5px', borderRadius: 8, border: '1px dashed #999', fontSize: 13, background: darkMode ? '#1a1d23' : '#fafafa' }}>
        <BankOutlined style={{ fontSize: 24, color: '#999', marginBottom: 4 }} />
        <div style={{ color: '#999' }}>Bu lokasyona ait platform verisi bulunamadı</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, width: '100%', marginTop: 8 }}>
      {organizedPlatforms.map((p) => {
        const tileWidths = p.kabinler.map(k => (k.kabin?.ikili || k.kabin?.isDouble) ? 124 : 60);
        const containerWidth = tileWidths.reduce((a, b) => a + b, 0) + ((tileWidths.length - 1) * 4);

        return (
          <div key={p.id} style={{ display: 'inline-block', borderRadius: 8, padding: 6, background: darkMode ? '#1e2024' : '#fff', border: darkMode ? '1px solid #2d3034' : '1px solid #e8e8e8' }}>
            {/* Platform Başlığı ve Butonlar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span onClick={() => navigate(`/platform_detay/${p.id}`)} style={{ fontSize: 10, cursor: 'pointer', color: darkMode ? '#ccc' : '#555' }}>#{p.id}</span>
              <Space size={2}>
                <Button type="text" size="small" icon={<EditOutlined style={{ fontSize: 12 }} />} onClick={() => navigate(`/platform_detay/${p.id}`)} />
                <Button type="text" size="small" icon={<DragOutlined style={{ fontSize: 12 }} />} onClick={() => onOpenFormModal && onOpenFormModal(75, 'Platform Taşıma', 'kabin_tasima', {}, {}, { kabin: p.id, nereden: currentLokasyon?.MaptriksID, ekleyen: user?.kullanici_no }, 'Insert')} />
              </Space>
            </div>

            {/* Kabinler */}
            <div style={{ display: 'flex', gap: 4, width: containerWidth }}>
              {p.kabinler.map((k) => {
                const kabin = k.kabin;
                const isDouble = !!(kabin?.ikili || kabin?.isDouble);

                return (
                  <div key={k.siraNo} style={{
                    height: 72, width: isDouble ? 124 : 60, minWidth: isDouble ? 124 : 60,
                    borderRadius: 6,
                    background: darkMode ? '#2a2d35' : '#fff',
                    border: !kabin ? '1px dashed #777' : `1px solid ${getDurumColor(kabin.durum)}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', overflow: 'hidden'
                  }}>
                    {!kabin ? (
                      <Tooltip title={(() => {
                        const e = p.kabinEnerji?.[k.siraNo] || p.kabinEnerji?.[String(k.siraNo)];
                        return (
                          <div style={{ minWidth: 120, fontSize: 12 }}>
                            <div><b>Kabin:</b> {e?.kabinNo || k.siraNo}</div>
                            <div><b>Sayaç:</b> {e?.sayacNo || '-'}</div>
                            <div><b>Abone:</b> {e?.aboneNo || '-'}</div>
                            <div><b>Banka:</b> -</div>
                          </div>
                        );
                      })()}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <BankOutlined style={{ fontSize: 16, color: '#999', marginBottom: 4 }} />
                          <Tag style={{ fontSize: 10, padding: '0 4px' }}>{k.siraNo} {currentLokasyon?.enerji ? '⚡' : '❌'}</Tag>
                        </div>
                      </Tooltip>
                    ) : (
                      <Tooltip title={
                        <div style={{ minWidth: 120, fontSize: 12 }}>
                          <div><b>Sayaç:</b> {kabin.sayacNo || p.kabinEnerji?.[k.siraNo]?.sayacNo || '-'}</div>
                          <div><b>Abone:</b> {kabin.aboneNo || p.kabinEnerji?.[k.siraNo]?.aboneNo || '-'}</div>
                          <div><b>Banka:</b> {kabin.bankaAdi || kabin.banka || '-'}</div>
                        </div>
                      }>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Tag color="default" style={{ marginBottom: 2, fontSize: 10, padding: '0 4px' }}>{kabin.kabinNo} {currentLokasyon?.enerji ? '⚡' : '❌'}</Tag>
                          {(() => {
                            const bankName = kabin.bankaAdi || kabin.banka || kabin.bankName;
                            const logo = getBankLogo(bankName);
                            return logo ?
                              <img src={logo} alt={bankName || ''} style={{ maxHeight: 40, maxWidth: 50, objectFit: 'contain' }} /> :
                              <div style={{ background: '#888', color: '#fff', padding: '2px', borderRadius: 3, fontSize: 9 }}>{bankName?.substring(0, 3) || '?'}</div>
                          })()}
                        </div>
                      </Tooltip>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CardDetailPage() {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { maptriksId } = useParams();
  const [loading, setLoading] = useState(true);
  const [lokasyon, setLokasyon] = useState(null);
  const [images, setImages] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [showAllImages, setShowAllImages] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [dataModal, setDataModal] = useState({ open: false, config: {} });
  // 25.08 md madde 1 — lokasyon + ilgili proje notları
  const [notlarOpen, setNotlarOpen] = useState(false);
  const [ysOdemeOpen, setYsOdemeOpen] = useState(false);
  const [formModal, setFormModal] = useState({ open: false, id: null, title: 'Form', data: {} });
  const [chartProjection, setChartProjection] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [offsiteModalOpen, setOffsiteModalOpen] = useState(false);
  const [offsiteData, setOffsiteData] = useState([]);
  const [offsiteLoading, setOffsiteLoading] = useState(false);
  const [offsiteInfo, setOffsiteInfo] = useState(null);
  const [sunumLoading, setSunumLoading] = useState(false);
  const [ysContracts, setYsContracts] = useState([]);
  const [ysLoading, setYsLoading] = useState(false);
  const { fetchAllContracts } = useContracts();

  const istatistikler = useMemo(() => {
    if (!lokasyon?.platforms) return { toplam: 0, dolu: 0, bos: 0 };
    let toplam = 0, dolu = 0;
    lokasyon.platforms.forEach(p => {
      toplam += (p.kabin_count || p.kabin || 0);
      dolu += (lokasyon.kabinler || []).filter(c => (c.platformId || c.platform_id) === p.id).length;
    });
    return { toplam, dolu, bos: Math.max(0, toplam - dolu) };
  }, [lokasyon]);

  useEffect(() => {
    const fetchChartData = async () => {
      if (!maptriksId) {
        setChartLoading(false);
        return;
      }
      setChartLoading(true);
      
      try {
        try {
          const chartResponse = await axios.get(
            `${API_BASE_URL}/ongoru/grafik/${maptriksId}?chart_type=birikimli_kar_zarar`,
            {
              headers: {
                ...(localStorage.getItem('auth_token') && {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                })
              }
            }
          );

          // 26.10 düzeltmesi: mali-rapor-bsk/ysk artık güncel sözleşme tablolarından okunuyor;
          // önceki grafikler eski 'sozlesmeler' tablosundan hesaplanmıştı ve önbellek süresiz
          // kullanıldığı için hiç yenilenmezdi. 27.10: tarih eşiği yerine sürüm işareti —
          // slice_config.kaynak MALI_RAPOR_KAYNAK değilse (eski kayıt, OngoruPage kaydı)
          // grafik yeniden hesaplanır ve işaretli olarak yeniden kaydedilir; deploy gününden bağımsız.
          if (chartResponse.data && chartResponse.data.success && chartResponse.data.data?.slice_config) {
            let config = chartResponse.data.data.slice_config;
            if (typeof config === 'string') {
              try {
                config = JSON.parse(config);
              } catch (parseError) {
                console.error('slice_config parse hatası:', parseError);
                config = null;
              }
            }
            if (config && config.kaynak !== MALI_RAPOR_KAYNAK) {
              config = null;
            }
            
            if (config && config.projection && Array.isArray(config.projection) && config.projection.length > 0) {
              try {
                const projection = config.projection.map(item => {
                  if (!item) return null;
                  return {
                    ...item,
                    date: item.date ? (item.date instanceof Date ? item.date : new Date(item.date)) : new Date()
                  };
                }).filter(item => item !== null);
                
                if (projection.length > 0) {
                  setChartProjection(projection);
                  setChartLoading(false);
                  return;
                }
              } catch (parseError) {
                console.error('Projection parse hatası:', parseError);
              }
            }
          }
        } catch (chartError) {
          if (chartError.response?.status !== 404) {
            console.error('Grafik yükleme hatası:', chartError);
          }
        }

        const { bankContracts, rentContracts } = await fetchAllContracts(maptriksId);

        if (bankContracts.length > 0 || rentContracts.length > 0) {
          const inflationRates = JSON.parse(localStorage.getItem('inflationRates') || '{}');
          const { startDate, endDate } = calculateDateRange(bankContracts, rentContracts);
          
          if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
            console.error('Geçersiz tarih aralığı hesaplandı!', {
              startDate: startDate?.format?.('YYYY-MM-DD') || startDate,
              endDate: endDate?.format?.('YYYY-MM-DD') || endDate,
              bankCount: bankContracts.length,
              rentCount: rentContracts.length
            });
            setChartProjection([]);
            setChartLoading(false);
            return;
          }
          
          const filteredBankContracts = bankContracts.filter(contract => {
            const contractStart = dayjs(contract.startDate);
            const contractEnd = contract.endDate ? dayjs(contract.endDate) : dayjs('2100-12-31');
            return (contractStart.isBefore(endDate) || contractStart.isSame(endDate)) && 
                   (contractEnd.isAfter(startDate) || contractEnd.isSame(startDate));
          });
          
          const filteredRentContracts = rentContracts.filter(contract => {
            const contractStart = dayjs(contract.startDate);
            const contractEnd = contract.endDate ? dayjs(contract.endDate) : dayjs('2100-12-31');
            return (contractStart.isBefore(endDate) || contractStart.isSame(endDate)) && 
                   (contractEnd.isAfter(startDate) || contractEnd.isSame(startDate));
          });
          
          const projection = generateProjection(filteredBankContracts, filteredRentContracts, startDate, endDate, inflationRates);
          setChartProjection(projection);
          
          if (projection.length > 0) {
            try {
              const formatDate = (date) => {
                const d = dayjs(date);
                return d.format('MMMM YYYY');
              };

              const chartData = {
                kaynak: MALI_RAPOR_KAYNAK,
                labels: projection.map(item => formatDate(item.date)),
                data: projection.map(item => item.cumulativeProfit || 0),
                projection: projection.map(item => ({
                  ...item,
                  date: item.date instanceof Date ? item.date.toISOString() : (item.date ? new Date(item.date).toISOString() : new Date().toISOString())
                }))
              };

              await axios.post(
                `${API_BASE_URL}/ongoru/grafik/kaydet`,
                {
                  maptriksID: parseInt(maptriksId),
                  chart_type: 'birikimli_kar_zarar',
                  slice_config: chartData
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    ...(localStorage.getItem('auth_token') && {
                      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    })
                  }
                }
              );
            } catch (saveError) {
              console.error('Grafik kaydetme hatası:', saveError.response?.data || saveError.message);
            }
          }
        } else {
          setChartProjection([]);
        }
      } catch (error) {
        console.error('Grafik oluşturma hatası:', error.response?.data || error.message);
        setChartProjection([]);
      } finally {
        setChartLoading(false);
      }
    };

    fetchChartData();
  }, [maptriksId, fetchAllContracts]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/atm/query?lokName=${maptriksId}`);
        const cabin = await axios.get(`${API_BASE_URL}/cabin/query?maptriksId=${maptriksId}`);
        let lokasyonData = response.data.data;
        let cabinData = cabin.data;
        if (Array.isArray(lokasyonData)) {
          lokasyonData = lokasyonData.find(item =>
            (item.MaptriksID ?? item.maptriksId ?? item.maptriksID ?? item.ID ?? item.id) == maptriksId
          ) || {};
        }
        const doluKabin = cabinData.doluKabinler?.[maptriksId] || 0;
        const bosKabin = lokasyonData.Boş_Kabin || 0;
        const maksKabin = lokasyonData.Maksimum_Olabilecek_Kabin_Adedi || 0;
        const genisletilebilir = maksKabin - (doluKabin + bosKabin);

        const processedLokasyon = {
          ID: lokasyonData.ID ?? lokasyonData.id ?? '',
          Name: lokasyonData.Name ?? '',
          Address: lokasyonData.Address ?? lokasyonData.Adres,
          İl: lokasyonData.İl,
          İlçe: lokasyonData.İlçe,
          Neighborhood: lokasyonData.Neighborhood,
          Adres: lokasyonData.Adres ?? lokasyonData.Address,
          Enlem: parseCoordinate(lokasyonData.Enlem),
          Boylam: parseCoordinate(lokasyonData.Boylam),
          lokasyonResim: lokasyonData.lokasyonResim,
          MaptriksID: lokasyonData.MaptriksID,
          CategoryName: lokasyonData.kategori,
          proje: lokasyonData.proje,
          enerji: lokasyonData.enerji,
          SubCategoryName: lokasyonData.kategori_name,
          Province: lokasyonData.Province,
          District: lokasyonData.District,
          durum_name: lokasyonData.durum_name,
          iptal_aciklama: lokasyonData.iptal_aciklama,
          kurulmaTarihi: lokasyonData.kurulmaTarihi,
          iptal_sebebi: lokasyonData.iptal_sebebi,
          sebep: lokasyonData.sebep,
          Dolu_Kabin_Sayısı: doluKabin,
          Boş_Kabin: bosKabin,
          Maksimum_Olabilecek_Kabin_Adedi: maksKabin,
          genisletilebilir: genisletilebilir,
          cabins: cabinData.cabins,
          kabinler: cabinData.cabins || [],
          platforms: cabinData.platforms || [],
          sebep: lokasyonData.sebep,
          talep_eden_banka: lokasyonData.talep_eden_banka,
          segment: lokasyonData.segment,
          genislik: lokasyonData.genislik,
          yukseklik: lokasyonData.yukseklik,
          derinlik: lokasyonData.derinlik,
          alan: lokasyonData.m2,
          dataref: lokasyonData.dataref,
          teknik: lokasyonData.teknik,
          yer_sahibi_ad: lokasyonData.yer_sahibi_ad,
          yer_sahibi_adres: lokasyonData.yer_sahibi_adres,
          yer_sahibi_tel: lokasyonData.yer_sahibi_tel,
          kira_tutar: lokasyonData.kira_tutar,
          odeme_sekli: lokasyonData.odeme_sekli,
          kdv_stopaj: lokasyonData.kdv_stopaj,
          komisyon: lokasyonData.komisyon,
          aciklama_kira: lokasyonData.aciklama_kira,
          aciklama: lokasyonData.aciklama,
          genelaciklama: lokasyonData.genelaciklama,
          ekleyen: lokasyonData.ekleyen,
          referans: lokasyonData.referans,
        };
        console.log("Proje", processedLokasyon.proje);
        setLokasyon(processedLokasyon);
        // 13.08: fotoğraf alanı büyütüldü — CDN çözünürlüğü de büyütülmezse bulanık görünür
        const imgs = getImageUrls(lokasyonData.lokasyonResim, lokasyonData.MaptriksID, { lowQualitySize: '1100:650', includeTarih: true });
        setImages(imgs);
        setActiveImage(0);
      } catch (error) {
        setLokasyon(null);
        setImages(null);
      } finally {
        setLoading(false);
      }
    };
    if (maptriksId) fetchData();
  }, [maptriksId, parseCoordinate]);

  // Yer Sahibi (YS) sözleşmelerinin temel bilgilerini doğrudan bu ekranda göster
  useEffect(() => {
    let mounted = true;
    async function fetchYs() {
      if (!maptriksId) return;
      try {
        setYsLoading(true);
        const res = await axios.post(`${API_BASE_URL}/list/execute-query/sozlesmeler`, {
          params: { maptriksId: Number(maptriksId) }
        });
        if (!mounted) return;
        const yer = (Array.isArray(res.data) ? res.data : []).filter(r => r.sozlesme_tipi === 'yer');
        setYsContracts(yer);
      } catch (e) {
        if (mounted) setYsContracts([]);
      } finally {
        if (mounted) setYsLoading(false);
      }
    }
    fetchYs();
    return () => { mounted = false; };
  }, [maptriksId]);

  // Yürürlükteki (aktif) YS sözleşmesi: imzalı (1/2/6) ve bitişi gelecekte olanlardan en güncel başlayan,
  // yoksa en son biten sözleşme (İmza etiketi Feshedildi vb. gösterir). 27.10: feshedilmiş sözleşme
  // (ör. 713) bitişi gelecekte diye yürürlükte seçilebiliyordu.
  const aktifYs = useMemo(() => {
    if (!ysContracts || ysContracts.length === 0) return null;
    const bugun = dayjs();
    const yururlukte = ysContracts.filter(c => [1, 2, 6].includes(Number(c['İmza']))
      && parseContractDate(c['Bit. Tar.'])?.isAfter(bugun));
    const havuz = yururlukte.length ? yururlukte : ysContracts;
    return [...havuz].sort((a, b) =>
      (parseContractDate(b['Baş. Tar.'])?.valueOf() || 0) - (parseContractDate(a['Baş. Tar.'])?.valueOf() || 0))[0];
  }, [ysContracts]);

  const handleOpenForm = (id, title, tableName, uniqueField, uniqueVal, extraData = {}, override = null) => {
    setFormModal({
      open: true, id, title,
      config: { tableName, uniqueField, uniqueValue: uniqueVal, extraData: { ...extraData, ...(override && { _override_function: override }) } }
    });
  };

  const handleOpenData = (apiUrl, title, queryParams) => {
    setDataModal({ open: true, config: { apiUrl, title, queryParams } });
  };

  const openOffsiteModal = async () => {
    setOffsiteModalOpen(true);
    try {
      setOffsiteLoading(true);
      const response = await axios.get(`${API_BASE_URL}/atm/offsite/${maptriksId}`);
      setOffsiteData(Array.isArray(response.data?.data) ? response.data.data : []);
      setOffsiteInfo({
        toplam: response.data?.toplam ?? 0,
        radius_m: response.data?.radius_m ?? 500,
        lokasyon: response.data?.lokasyon || null,
      });
    } catch (error) {
      setOffsiteData([]);
      setOffsiteInfo(null);
      Modal.error({
        title: 'Offsite ATM bilgileri alınamadı',
        content: error.response?.data?.detail || error.message,
      });
    } finally {
      setOffsiteLoading(false);
    }
  };

  const createPdfPresentation = async () => {
    try {
      setSunumLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/sunum/generate-pdf-sunum/${maptriksId}`,
        {},
        {
          headers: {
            ...(localStorage.getItem('auth_token') && {
              Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            }),
          },
        }
      );
      Modal.success({
        title: 'PDF sunum oluşturuldu',
        content: response.data?.mail_scheduled
          ? 'PDF kaydedildi ve e-posta gönderimi başlatıldı.'
          : 'PDF kaydedildi. Kullanıcı e-posta adresi bulunamadığı için e-posta gönderilemedi.',
        okText: 'PDF’i Aç',
        onOk: () => window.open(`${API_BASE_URL}/sunum/download-pdf-sunum/${maptriksId}`, '_blank', 'noopener,noreferrer'),
      });
    } catch (error) {
      Modal.error({
        title: 'PDF sunum oluşturulamadı',
        content: error.response?.data?.detail || error.message,
      });
    } finally {
      setSunumLoading(false);
    }
  };

  // Koordinat yoksa (null/NaN veya 0,0) "Konumu Kopyala" pasif kalır; 0,0 Maptriks'te "bilinmiyor" anlamına geliyor
  const konumVar = Number.isFinite(lokasyon?.Enlem) && Number.isFinite(lokasyon?.Boylam)
    && !(lokasyon.Enlem === 0 && lokasyon.Boylam === 0);

  const konumuKopyala = async () => {
    if (!konumVar) return;
    const link = `https://www.google.com/maps?q=${lokasyon.Enlem},${lokasyon.Boylam}`;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API desteklenmiyor');
      await navigator.clipboard.writeText(link);
      message.success('Google Maps linki kopyalandı');
    } catch (err) {
      // Clipboard API yok ya da izin vermedi (HTTP, eski tarayıcı): geçici textarea ile eski yöntem
      try {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('execCommand copy başarısız');
        message.success('Google Maps linki kopyalandı');
      } catch (err2) {
        message.error('Google Maps linki kopyalanamadı');
      }
    }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><Spin size="large" /></div>;
  if (!lokasyon) return <Alert message="Lokasyon bulunamadı" type="error" showIcon style={{ margin: 24 }} />;

  return (
    <div style={{ padding: '12px 24px', background: darkMode ? '#03070e' : '#f5f5f5', minHeight: '100vh', color: darkMode ? '#e0e0e0' : 'inherit' }}>
      {/* 13.08: detay sayfası full width (önceden maxWidth: 1200 ile sınırlıydı) */}
      <div style={{ width: '100%', margin: '0 auto' }}>

        {/* Modals */}
        {/* maptriksId olmadan modal "Lokasyon ID bulunamadı" verir (sil/sırala PUT /atm/{id});
            onImagesChange olmadan da kaydedilen sıra sayfa yenilenene kadar görünmez. */}
        <ImageModal
          open={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          images={images}
          startIndex={activeImage}
          maptriksId={lokasyon.MaptriksID}
          onImagesChange={setImages}
          onActiveImageChange={setActiveImage}
        />
        <DataListModal open={dataModal.open} onClose={() => setDataModal({ ...dataModal, open: false })} {...dataModal.config} />
        <NotlarModal
          open={notlarOpen}
          onClose={() => setNotlarOpen(false)}
          maptriksId={lokasyon.MaptriksID}
          kullaniciNo={user?.kullanici_no}
        />
        {/* 27.10 (F4 YS): YS kira hakedişi / ödeme paneli asıl lokasyon detayında da (salt-okur) */}
        <Modal
          title={`YS Kira Faturaları ve Ödemeleri — ${lokasyon.MaptriksID}`}
          open={ysOdemeOpen}
          onCancel={() => setYsOdemeOpen(false)}
          footer={null}
          width={1200}
          destroyOnHidden
        >
          <YsOdemeleriPanel maptriksId={lokasyon.MaptriksID} variant="borderless" />
        </Modal>
        <Modal
          title={
            <Space>
              <BankOutlined />
              <span>Offsite ATM'ler</span>
              {offsiteInfo && <Tag color="blue">{offsiteInfo.toplam} ATM · {offsiteInfo.radius_m} m</Tag>}
            </Space>
          }
          open={offsiteModalOpen}
          onCancel={() => setOffsiteModalOpen(false)}
          footer={null}
          width={820}
          destroyOnHidden
        >
          <OffsiteMap location={offsiteInfo?.lokasyon} atms={offsiteData} />
          <Table
            rowKey={(record, index) => `${record.bankId}-${record.latitude}-${record.longitude}-${index}`}
            size="small"
            loading={offsiteLoading}
            dataSource={offsiteData}
            scroll={{ x: 'max-content', y: 420 }}
            pagination={{ pageSize: 20, showTotal: total => `Toplam ${total} offsite ATM` }}
            locale={{ emptyText: 'Bu lokasyonun çevresinde offsite ATM bulunamadı' }}
            columns={[
              { title: 'Banka', dataIndex: 'banka', key: 'banka', width: 180, render: value => <strong>{value || '-'}</strong> },
              { title: 'Adres', dataIndex: 'adres', key: 'adres', width: 260, ellipsis: true, render: value => value || '-' },
              { title: 'Mahalle', dataIndex: 'mahalle', key: 'mahalle', width: 150, render: value => value || '-' },
              { title: 'İlçe', dataIndex: 'ilce', key: 'ilce', width: 130, render: value => value || '-' },
              {
                title: 'Mesafe', dataIndex: 'mesafe_m', key: 'mesafe', align: 'right', width: 100,
                sorter: (a, b) => a.mesafe_m - b.mesafe_m,
                render: value => <Tag color={value <= 150 ? 'green' : value <= 300 ? 'gold' : 'default'}>{value} m</Tag>,
              },
            ]}
          />
        </Modal>
        <Modal
          title={formModal.title} open={formModal.open} footer={null} width={800} destroyOnHidden
          onCancel={() => setFormModal({ ...formModal, open: false })}
        >
          {formModal.open && <FormikForm formId={formModal.id} {...formModal.config} onSuccess={() => window.location.reload()} onCancel={() => setFormModal({ ...formModal, open: false })} />}
        </Modal>

        {/* Breadcrumb */}
        <Breadcrumb style={{ marginBottom: 12 }} items={[
          { title: <span onClick={() => navigate('/home')}><HomeOutlined /> Ana Sayfa</span>, cursor: 'pointer' },
          { title: <span onClick={() => navigate('/map')}><EnvironmentOutlined /> Lokasyonlar</span>, cursor: 'pointer' },
          { title: lokasyon.İl }, { title: lokasyon.İlçe }, { title: lokasyon.Neighborhood }
        ]} />

        <Card style={{ borderRadius: 12, background: darkMode ? '#23272f' : '#fff', border: darkMode ? '1px solid #333' : undefined }}>
          <Row gutter={[24, 24]}>
            {/* Sol: Görseller — 13.08: alan büyütüldü (md 12→14, sabit 250px yerine oranlı) */}
            <Col xs={24} md={14}>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                {images.length > 0 ? (
                  <div style={{ aspectRatio: '16 / 9', minHeight: 250, width: '100%', background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
                    <Image
                      src={images[activeImage]?.url}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      preview={false}
                      onClick={() => setIsImageModalOpen(true)}
                    />
                    {images[activeImage]?.tarih && <Tag color="black" style={{ position: 'absolute', top: 8, right: 8, opacity: 0.7 }}>{images[activeImage].tarih}</Tag>}
                    {images.length > 1 && (
                      <>
                        <Button icon={<LeftOutlined />} shape="circle" style={{ position: 'absolute', left: 10, top: '45%' }} onClick={() => setActiveImage(p => (p - 1 + images.length) % images.length)} />
                        <Button icon={<RightOutlined />} shape="circle" style={{ position: 'absolute', right: 10, top: '45%' }} onClick={() => setActiveImage(p => (p + 1) % images.length)} />
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ aspectRatio: '16 / 9', minHeight: 250, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EnvironmentOutlined style={{ fontSize: 48, color: '#fff' }} />
                  </div>
                )}
              </div>
              {/* Thumbnails */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(showAllImages ? images : images.slice(0, 8)).map((img, idx) => (
                  <div key={idx} onClick={() => setActiveImage(idx)} style={{ border: activeImage === idx ? '2px solid #1890ff' : '2px solid transparent', cursor: 'pointer', borderRadius: 4 }}>
                    <img src={img.url} style={{ width: 84, height: 60, objectFit: 'cover', borderRadius: 2 }} alt="thumb" />
                  </div>
                ))}
                {!showAllImages && images.length > 8 && (
                  <Button size="small" icon={<PlusOutlined />} onClick={() => setIsImageModalOpen(true)}>+{images.length - 8}</Button>
                )}
              </div>

              {/* Aksiyon Butonları */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                <Button icon={<BankOutlined />} onClick={openOffsiteModal}>Offsite ATM</Button>
                <Button icon={<FileTextOutlined />} onClick={() => setNotlarOpen(true)}>Notlar</Button>
                <Button type="primary" icon={<FilePdfOutlined />} loading={sunumLoading} onClick={createPdfPresentation}>PDF Sunum Oluştur</Button>
                {[
                  { label: 'Sunumlar', action: 'sunumlar' }, { label: 'Sözleşme', action: 'sozlesmeler' },
                  { label: 'Maliyet', action: 'maliyetler' }, { label: 'Fatura', action: 'faturalar' }
                ].map(btn => (
                  <Button key={btn.label} className={darkMode ? 'btn-pivot-dark' : 'btn-pivot-light'} onClick={() => handleOpenData(`${API_BASE_URL}/list/execute-query/${btn.action}`, `${btn.label} Bilgileri`, { maptriksId: lokasyon.MaptriksID })}>{btn.label}</Button>
                ))}
                <Button className={darkMode ? 'btn-pivot-dark' : 'btn-pivot-light'} onClick={() => setYsOdemeOpen(true)}>YS Ödemeleri</Button>
                <Button className={darkMode ? 'btn-pivot-dark' : 'btn-pivot-light'} onClick={() => navigate(`/map/${lokasyon.MaptriksID}`, { state: { selectedLocation: { lat: lokasyon.Enlem, lng: lokasyon.Boylam, zoom: 17 } } })}>Harita</Button>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'lokasyon',
                        label: 'Lokasyon Bazlı Mali Rapor',
                      },
                      ...(lokasyon.proje ? [{
                        key: 'proje',
                        label: 'Proje Bazlı Mali Rapor',
                      }] : []),
                    ],
                    onClick: ({ key }) => {
                      if (key === 'lokasyon') {
                        navigate(`/mali_rapor/${lokasyon.MaptriksID}`);
                      } else if (key === 'proje') {
                        if (lokasyon.proje) {
                          navigate(`/mali_rapor?prj=${lokasyon.proje}`);
                        } else {
                          console.warn('Bu lokasyona ait proje bilgisi bulunamadı');
                        }
                      }
                    }
                  }}
                  trigger={['click']}
                >
                  <Button className={darkMode ? 'btn-pivot-dark' : 'btn-pivot-light'} icon={<DownOutlined />}>
                    Mali Rapor
                  </Button>
                </Dropdown>
              </div>
            </Col>

            {/* Sağ: Bilgiler ve Grafik */}
            <Col xs={24} md={10}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Title copyable={{
                  text: `${lokasyon.Name} ${lokasyon.MaptriksID}`,
                  icon: [
                    <span style={{ fontSize: 16 }} key="copy">
                      <svg width="24" height="24" viewBox="0 0 1024 1024" fill="currentColor"><path d="M832 112H296c-4.4 0-8 3.6-8 8v64h64V184h480v656H352v-56h-64v64c0 4.4 3.6 8 8 8h536c4.4 0 8-3.6 8-8V120c0-4.4-3.6-8-8-8z" /><path d="M704 288H192c-17.7 0-32 14.3-32 32v560c0 17.7 14.3 32 32 32h512c17.7 0 32-14.3 32-32V320c0-17.7-14.3-32-32-32zm0 592H192V320h512v560z" /></svg>
                    </span>,
                    <span style={{ fontSize: 16 }} key="copied">
                      <svg width="24" height="24" viewBox="0 0 1024 1024" fill="currentColor"><path d="M832 112H296c-4.4 0-8 3.6-8 8v64h64V184h480v656H352v-56h-64v64c0 4.4 3.6 8 8 8h536c4.4 0 8-3.6 8-8V120c0-4.4-3.6-8-8-8z" /><path d="M704 288H192c-17.7 0-32 14.3-32 32v560c0 17.7 14.3 32 32 32h512c17.7 0 32-14.3 32-32V320c0-17.7-14.3-32-32-32zm0 592H192V320h512v560z" /></svg>
                    </span>
                  ],
                }}
                  level={1}
                  style={{
                    margin: 0,
                    padding: 0,
                    fontSize: 28,
                    background: 'none',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    lineHeight: 1.2
                  }}>{lokasyon.Name}</Title>
                <Space>
                  <Button type="text" icon={<ToolOutlined style={{ fontSize: 20 }} />} onClick={() => handleOpenForm(210, 'Kurulum', 'kurulum_bilgileri', 'lokasyon', lokasyon.MaptriksID, { ekleyen: user?.kullanici_no }, 'Insert')} />
                  <Button type="text" icon={<EditOutlined style={{ fontSize: 20 }} />} onClick={() => navigate(`/edit/location_detail/${lokasyon.MaptriksID}`)} />
                  {lokasyon.durum_name && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      marginLeft: 'auto'
                    }}>
                      {trIncludes(lokasyon.durum_name, 'aktif') ? (
                        <svg width="22" height="22" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" fill="#4CAF50" />
                        </svg>
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" fill="#F44336" />
                        </svg>
                      )}
                    </span>
                  )}
                </Space>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Paragraph copyable style={{ fontSize: 15, margin: 0, display: 'inline' }}>
                  {lokasyon.Address}
                </Paragraph>
                {/* Adres kopyalama ikonu metni, bu ikon Google Maps linkini kopyalar */}
                <Tooltip title={konumVar ? 'Google Maps linkini kopyala' : 'Koordinat bilgisi yok'}>
                  <Button type="text" size="small" icon={<EnvironmentOutlined />} disabled={!konumVar} onClick={konumuKopyala} aria-label="Google Maps linkini kopyala" />
                </Tooltip>
              </div>
              <Space style={{ marginBottom: 16 }}>
                <Tag color={'pink'} icon={<InfoCircleOutlined />}>
                  {lokasyon.MaptriksID}
                </Tag>
                {lokasyon.CategoryName && (
                  <Tag color="blue" icon={<TagOutlined />}>
                    {lokasyon.CategoryName}
                  </Tag>
                )}
                {lokasyon.SubCategoryName && (
                  <Tag color="purple" icon={<TagOutlined />}>
                    {lokasyon.SubCategoryName}
                  </Tag>
                )}
              </Space>

              {/* Grafikler Alanı */}
              <div style={{ background: darkMode ? '#1f1f1f' : '#f9f9f9', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}>
                {/* Kar/Zarar Canvas */}
                <div style={{ height: 160, marginBottom: 10, cursor: 'pointer', position: 'relative' }} onClick={() => navigate(`/ongoru/${lokasyon.MaptriksID}`)}>
                  {chartLoading ? (
                    <div style={{ 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: darkMode ? '#e0e0e0' : '#999'
                    }}>
                      Yükleniyor...
                    </div>
                  ) : (
                    <CumulativeProfitChart 
                      maptriksId={lokasyon.MaptriksID}
                      projection={chartProjection}
                      autoSave={false}
                    />
                  )}
                </div>

                {/* Doluluk Oranı Bar */}
                {istatistikler.toplam > 0 && (
                  <div style={{ display: 'flex', height: 24, borderRadius: 4, overflow: 'hidden', background: '#ddd' }}>
                    <div style={{ width: `${(istatistikler.dolu / istatistikler.toplam) * 100}%`, background: '#52c41a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>
                      {istatistikler.dolu > 0 && istatistikler.dolu}
                    </div>
                    <div style={{ flex: 1, background: '#ff4d4f', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>
                      {istatistikler.bos > 0 && istatistikler.bos}
                    </div>
                  </div>
                )}
                <div style={{ textAlign: 'center', fontSize: 12, marginTop: 4 }}>Toplam: {istatistikler.toplam}</div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Platform Bilgileri */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <FileDoneOutlined /> <span style={{ fontWeight: 600 }}>Platform Bilgileri</span>
          </div>
          <KabinlerListesi
            data={lokasyon.kabinler}
            platforms={lokasyon.platforms}
            user={user}
            onOpenFormModal={handleOpenForm}
            currentLokasyon={lokasyon}
          />
        </div>

        {/* Yer Sahibi (YS) Sözleşmesi — yürürlükteki sözleşmenin temel bilgileri (küçük kart) */}
        {aktifYs && (
          <Card
            size="small"
            style={{ marginTop: 16, borderRadius: 12, maxWidth: 440, background: darkMode ? '#23272f' : '#fff', border: darkMode ? '1px solid #333' : undefined }}
            title={<span><BankOutlined /> <span style={{ fontWeight: 600 }}>Yer Sahibi Sözleşmesi</span></span>}
            extra={aktifYs.ID ? (
              <Button type="link" size="small" onClick={() => navigate(`/yer_sozlesme_detay/${aktifYs.ID}`, { state: { record: { ...aktifYs, id: aktifYs.ID } } })}>Detay</Button>
            ) : null}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Yer Sahibi">{aktifYs['Ünvan'] || '-'}</Descriptions.Item>
              <Descriptions.Item label="Kira">
                {(aktifYs['Kira'] != null && aktifYs['Kira'] !== '') ? formatMoney(aktifYs['Kira'], aktifYs['Döviz'] || 'TRY') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Başlangıç">{parseContractDate(aktifYs['Baş. Tar.'])?.format('DD.MM.YYYY') || '-'}</Descriptions.Item>
              <Descriptions.Item label="Bitiş">{parseContractDate(aktifYs['Bit. Tar.'])?.format('DD.MM.YYYY') || '-'}</Descriptions.Item>
              <Descriptions.Item label="Ödeme"><Tag color="blue">{contractCodeLabel(aktifYs['Ödeme'], ODEME_TIPLERI)}</Tag></Descriptions.Item>
              <Descriptions.Item label="İmza">
                {aktifYs['İmza'] !== null && aktifYs['İmza'] !== undefined && aktifYs['İmza'] !== '' ? (
                  <Tag color={String(contractCodeLabel(aktifYs['İmza'], SOZLESME_IMZA_DURUMU)).includes('İmzalandı') ? 'green' : 'default'}>
                    {contractCodeLabel(aktifYs['İmza'], SOZLESME_IMZA_DURUMU)}
                  </Tag>
                ) : <Tag>Belirtilmemiş</Tag>}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </div>
    </div>
  );
}

export default CardDetailPage;